import Image from '@atoms/Image/Image';
import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IImage } from '@models/IImage';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import slugify from '@utils/helpers/slugify';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import SwiperType from 'swiper';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/types';

interface Props {
  readonly productImages: [IImage, ...IImage[]];
}

const ProductDetailsGalleryDesktop = ({ productImages }: Props) => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState(0);
  const desktopImagesCarouselNavId =
    'product-images-navigation-carousel-navigation-id';
  const hasMoreItemsToShow =
    currentSlidesPerView > 0 && productImages.length > currentSlidesPerView;
  const loopProductImages = duplicateArrayItems(
    productImages,
    Math.ceil(currentSlidesPerView + 1) * 2
  );
  const displayedProductImages = hasMoreItemsToShow
    ? loopProductImages
    : productImages;
  const visibleSlidesIndexes = Array.from({
    length: Math.ceil(currentSlidesPerView),
  }).reduce<number[]>(
    (acc, _, index) => [
      ...acc,
      (currentSlideIndex + index) % displayedProductImages.length,
    ],
    []
  );

  const showPreviousImage = () => {
    const currentImageIndex = visibleSlidesIndexes.includes(selectedImageIndex)
      ? selectedImageIndex
      : visibleSlidesIndexes[0]!;
    const newImageIndex =
      currentImageIndex === 0
        ? displayedProductImages.length - 1
        : currentImageIndex - 1;
    setSelectedImageIndex(newImageIndex);
  };

  const showNextImage = () => {
    const currentImageIndex = visibleSlidesIndexes.includes(selectedImageIndex)
      ? selectedImageIndex
      : visibleSlidesIndexes[0]!;
    const newImageIndex =
      currentImageIndex === displayedProductImages.length - 1
        ? 0
        : currentImageIndex + 1;
    setSelectedImageIndex(newImageIndex);
  };

  const handleSwiperUpdate = (swiper: SwiperType) => {
    const slidesPerView = swiper?.params?.slidesPerView;

    setCurrentSlidesPerView(
      slidesPerView && typeof slidesPerView === 'number' ? slidesPerView : 0
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    // Whenever the length of displayedProductImages changes,
    // reset the selected image index to 0.
    setSelectedImageIndex(0);
  }, [displayedProductImages.length]);

  return (
    <div
      className="flex flex-col gap-y-[15px]"
      data-testid="CT-15-Product-Details-Gallery-Desktop"
    >
      <button
        className="flex h-[450px] rounded-[10px] bg-grey-100 p-15 xl:h-[524px] 2xl:h-[674px]"
        onClick={() => {
          swiperInstance?.slideNext();
          showNextImage();
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={displayedProductImages[selectedImageIndex]?.desktop?.url}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="w-full"
          >
            <Image
              image={displayedProductImages[selectedImageIndex]}
              imageClasses="object-contain h-full"
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-15-Product-Details-Gallery-Desktop-Main-Image"
            />
          </motion.div>
        </AnimatePresence>
      </button>

      {productImages.length > 1 && (
        <div className="flex gap-x-5">
          <div className="w-full min-w-0 grow">
            <ShadowContainerHorizontal disableScroll>
              <Carousel
                id="product-images-navigation-carousel-id"
                className="whitespace-normal"
                breakpoints={{
                  1024: {
                    slidesPerView: 2.5,
                  },
                  1440: {
                    slidesPerView: 3.5,
                  },
                  1920: {
                    slidesPerView: 4.5,
                  },
                }}
                spaceBetween={10}
                loop
                navId={desktopImagesCarouselNavId}
                onResize={(swiper) => {
                  handleSwiperUpdate(swiper);
                  setSelectedImageIndex(0);
                }}
                onInit={handleSwiperUpdate}
                onSlideChangeTransitionEnd={(swiper) => {
                  setCurrentSlideIndex(swiper?.realIndex || 0);
                }}
                onSwiper={setSwiperInstance}
              >
                {displayedProductImages.map((image, index) => (
                  <SwiperSlide
                    className="stretch-card"
                    key={slugify(`${image.desktop?.url}-${index}`)}
                  >
                    <button
                      onClick={() => {
                        setSelectedImageIndex(index);
                      }}
                      className={classNames(
                        'h-[110px] rounded-[10px] bg-grey-100 p-2.5',
                        {
                          'border border-yellow': index === selectedImageIndex,
                        }
                      )}
                    >
                      <Image
                        image={image}
                        imageClasses="object-contain h-full"
                        dataTestId={`CT-15-Product-Details-Gallery-Desktop-Product-Image-${index}`}
                      />
                    </button>
                  </SwiperSlide>
                ))}
              </Carousel>
            </ShadowContainerHorizontal>
          </div>

          <div
            className={classNames(
              'shrink-0 items-center gap-x-2.5',
              hasMoreItemsToShow ? 'flex' : 'hidden'
            )}
          >
            <NavButton
              direction={HorizontalDirectionEnum.Left}
              id={desktopImagesCarouselNavId}
              onClick={showPreviousImage}
            />

            <NavButton
              direction={HorizontalDirectionEnum.Right}
              id={desktopImagesCarouselNavId}
              onClick={showNextImage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsGalleryDesktop;
